import { Controller } from "@hotwired/stimulus"

// Shared controller for inline reel players.
// Handles activation, deactivation, and playback inside horizontal scrollers.
export default class extends Controller {
  static targets = ["overlay", "content", "player"]
  static values = {
    source: String,
    timestamp: Number,
  }

  connect() {
    if (this.isActive()) {
      this.setActive(true)
    } else {
      this.deactivate()
    }

    this.boundExternal = this.onExternalActivate.bind(this)
    window.addEventListener("reel:activated", this.boundExternal)
  }

  disconnect() {
    if (this.boundExternal) {
      window.removeEventListener("reel:activated", this.boundExternal)
    }
  }

  activate(event) {
    const interactive = event.target.closest("a,button,label,input,[role='button'],media-controls,media-play-button,media-mute-button,media-fullscreen-button,media-time-slider,media-menu-button,media-seek-button")
    if (interactive && interactive !== this.element) return

    event.preventDefault()
    if (this.isActive()) return

    window.dispatchEvent(new CustomEvent("reel:activated", { detail: { element: this.element } }))
    this.setActive(true)
    this.scrollToCenter()
    this.play()
  }

  // Called by parent controllers to open a card without playing
  open() {
    if (this.isActive()) return

    window.dispatchEvent(new CustomEvent("reel:activated", { detail: { element: this.element } }))
    this.setActive(true)
  }

  onExternalActivate(event) {
    if (event.detail && event.detail.element !== this.element) {
      this.deactivate()
    }
  }

  isActive() {
    return this.element.dataset.active === "true"
  }

  setActive(state) {
    const val = state ? "true" : "false"
    this.element.dataset.active = val
    ;(this.overlayTargets || []).forEach(el => el.dataset.active = val)
    ;(this.contentTargets || []).forEach(el => el.dataset.active = val)
    ;(this.playerTargets || []).forEach(el => el.dataset.active = val)
  }

  deactivate() {
    this.setActive(false)
    this.pause()
  }

  play() {
    const mp = this.findMediaPlayer()
    if (!mp) return

    const seconds = this.hasTimestampValue ? this.timestampValue : 0
    const source = this.hasSourceValue ? this.sourceValue : null

    mp.dispatchEvent(new CustomEvent("video-player:external:seekandplay", {
      bubbles: false,
      detail: { seconds, source }
    }))
  }

  pause() {
    const mp = this.findMediaPlayer()
    if (!mp) return

    mp.dispatchEvent(new CustomEvent("video-player:external:pause", { bubbles: false }))
  }

  findMediaPlayer() {
    return this.element.querySelector("media-player[data-controller~='video-player']")
  }

  // Center the card in the horizontal scroller
  scrollToCenter() {
    this.element.scrollIntoView({ block: "nearest", inline: "center", behavior: "smooth" })
  }
}
